import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  ConfirmationModal,
  Header,
  Pagination,
  Tabs,
} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import {appPaths} from '@/config/appPaths';
import type {AdminBusinessUpdateListItem} from '@/types';
import {BusinessUpdateStatus} from '@/types';

import ProductUpdatesTable from './ProductUpdatesTable';
import useProductUpdatesList from './useProductUpdatesList';

const STATUS_TABS: {
  id: string;
  title: string;
  status?: BusinessUpdateStatus;
}[] = [
  {id: 'all', title: 'All', status: undefined},
  {
    id: 'published',
    title: 'Published',
    status: BusinessUpdateStatus.Published,
  },
  {
    id: 'scheduled',
    title: 'Scheduled',
    status: BusinessUpdateStatus.Scheduled,
  },
  {id: 'drafts', title: 'Drafts', status: BusinessUpdateStatus.Draft},
];

const ProductUpdatesList = () => {
  const navigate = useNavigate();
  const {
    list,
    total,
    page,
    setPage,
    isLoading,
    handleStatusChange,
    handleDelete,
  } = useProductUpdatesList();
  const [deleteTarget, setDeleteTarget] =
    useState<AdminBusinessUpdateListItem>();
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    // handleDelete surfaces its own success and failure messages.
    await handleDelete(deleteTarget.id);
    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <div className="flex items-start justify-between">
        <Header title="Product Updates">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Author and publish updates to the Business Portal notification
            panel.
          </p>
        </Header>
        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessUpdateManage}
        >
          <Button
            variant="solid"
            colorScheme="cyan"
            // Leading slash needed, see goToList in useProductUpdateCompose.
            onClick={() => navigate(`/${appPaths.productUpdates}/new`)}
          >
            + New update
          </Button>
        </PermissionsProvider>
      </div>

      <section className="pt-5">
        <Tabs
          tabs={STATUS_TABS.map((tab) => ({
            id: tab.id,
            title: tab.title,
            content: null,
          }))}
          onTabChange={(tab) => {
            const match = STATUS_TABS.find((t) => t.id === tab.id);
            handleStatusChange(match?.status);
          }}
        />
      </section>

      <section className="pt-5">
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <ProductUpdatesTable items={list} onDelete={setDeleteTarget} />
        )}
      </section>

      {list.length ? (
        <Pagination
          total={total}
          currentPageOverride={page + 1}
          onPageChange={({page: nextPage}) => {
            // Pagination already converts to the 0-indexed `page` field.
            setPage(nextPage ?? 0);
            return Promise.resolve();
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Delete update"
        label={`Delete "${deleteTarget?.title ?? ''}"? This cannot be undone.`}
        confirmAction={() => confirmDelete().catch(() => {})}
        confirmActionLabel="Delete"
        cancelAction={() => setDeleteTarget(undefined)}
        cancelActionLabel="Cancel"
        loading={isDeleting}
        loadingLabel="Deleting..."
      />
    </main>
  );
};

export default ProductUpdatesList;
